/**
 * cleanupDuplicatePhaseOps.js
 *
 * FT-219: Remove duplicate phase operations from voyage_settings
 *
 * PROBLEM: When initializeVesselStorage() set voyage_settings = [] for new vessels,
 * VoyageSettingsModal took the "existing data" branch ([] is truthy) and called
 * ensurePhaseOperations() which added phase ops. Later, something added them again,
 * causing duplicate Pre-Arrival, Pre-Departure, and Post-Arrival entries.
 *
 * FIX: The source fix prevents future duplicates by not initializing voyage_settings
 * (leaving it null). This migration cleans up existing duplicates in affected vessels.
 *
 * This is a ONE-TIME migration that:
 * 1. Scans all localStorage keys matching /^voyage_settings_/
 * 2. For each key, loads voyage_settings and checks operationsList
 * 3. Deduplicates by phaseType (keeps first occurrence)
 * 4. Saves back if changed
 * 5. Sets migration flag to prevent re-running
 */

const MIGRATION_FLAG = 'sr.migration.cleanupDuplicatePhaseOps_v4';

/**
 * Run cleanup for duplicate phase operations in voyage_settings
 * @returns {boolean} - True if migration ran, false if skipped
 */
export const cleanupDuplicatePhaseOps = () => {
  // Check if already migrated
  if (localStorage.getItem(MIGRATION_FLAG) === 'complete') {
    return false;
  }

  try {
    console.log('[FT-219] Starting duplicate phase operations cleanup...');

    let cleanedCount = 0;
    let keysChecked = 0;

    // Scan all localStorage keys directly (avoids timing issues with vessels list)
    const voyageSettingsKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('voyage_settings_')) {
        voyageSettingsKeys.push(key);
      }
    }

    if (voyageSettingsKeys.length === 0) {
      console.log('[FT-219] No voyage_settings keys found, skipping migration');
      localStorage.setItem(MIGRATION_FLAG, 'complete');
      return true;
    }

    // Process each voyage_settings key
    for (const settingsKey of voyageSettingsKeys) {
      keysChecked++;

      const settingsJson = localStorage.getItem(settingsKey);
      if (!settingsJson) {
        continue;
      }

      let settings;
      try {
        settings = JSON.parse(settingsJson);
      } catch (e) {
        console.warn(`[FT-219] Failed to parse ${settingsKey}:`, e);
        continue;
      }

      // Check if operationsList exists and is an array
      if (!settings || !settings.operationsList || !Array.isArray(settings.operationsList)) {
        continue;
      }

      const operationsList = settings.operationsList;

      // Find and remove duplicates by phaseType
      const seenPhaseTypes = new Set();
      const deduplicatedList = [];
      let removedCount = 0;

      for (const op of operationsList) {
        if (op.phaseType) {
          // This is a phase operation
          if (seenPhaseTypes.has(op.phaseType)) {
            // Duplicate - skip it
            removedCount++;
            console.log(`[FT-219] Removing duplicate ${op.phaseType} from ${settingsKey}`);
          } else {
            // First occurrence - keep it
            seenPhaseTypes.add(op.phaseType);
            deduplicatedList.push(op);
          }
        } else {
          // Not a phase operation - keep it
          deduplicatedList.push(op);
        }
      }

      // Save back if we removed duplicates
      if (removedCount > 0) {
        settings.operationsList = deduplicatedList;
        localStorage.setItem(settingsKey, JSON.stringify(settings));
        cleanedCount++;
        console.log(`[FT-219] Cleaned ${removedCount} duplicate(s) from ${settingsKey}`);
      }
    }

    // Mark migration complete
    localStorage.setItem(MIGRATION_FLAG, 'complete');

    if (cleanedCount > 0) {
      console.log(`[FT-219] Cleanup complete: ${cleanedCount} key(s) cleaned, ${keysChecked} checked`);
    } else {
      console.log(`[FT-219] No duplicate phase operations found in ${keysChecked} key(s)`);
    }

    return true;

  } catch (error) {
    console.error('[FT-219] Cleanup failed:', error);
    // Still set flag - we don't want to keep retrying if there's an error
    localStorage.setItem(MIGRATION_FLAG, 'complete');
    return false;
  }
};
